<script>

/**
 * Vue: Main glossary (main-glossary)
 * -----------------------------------------------------------------------------
 * Glossary main section.
 * - Static section.
 *
 */
import { debounce } from '~/helpers/general'
import classes from '~/config/classes'

export default {
  name: 'MainGlossary',

  props: {
    sectionId: {
      type: String,
      default: '',
    },
  },

  data() {
    return {
      activeButton: null,
    }
  },

  mounted() {
    this.setSelectors()
    this.scrollToSection()
    this.keysHeaderHeight()
    this.disableUnmatchedButtons()
    window.addEventListener('resize', debounce(() => this.keysHeaderHeight(), 250))
  },

  methods: {

    /**
     * Set selectors.
     */
    setSelectors() {
      const container = document.querySelector(
        `#vue-element [js-main-glossary="${this.sectionId}"]`,
      )

      this.selectors = {
        container,
        buttons: [...container.querySelectorAll('[js-main-glossary="button"]')],
        items: [...container.querySelectorAll('[js-main-glossary="item"]')],
        keys: container.querySelector('[js-main-glossary="keys"]'),
      }
    },

    /**
     * Find coresponded item element.
     * @param {String} target - Target letter.
     * @return {*}
     */
    findTargetElement(target) {
      if (!target) {
        return null
      }

      if (target === 'numbers') {
        return this.selectors.items.find(item => (/\d/).test(item.id))
      }

      return this.selectors.items.find(
        item => item.id.charAt(0).toLowerCase() === target.toLowerCase(),
      )
    },

    /**
     * Disable buttons without matching items.
     */
    disableUnmatchedButtons() {
      this.selectors.buttons.forEach(button => {
        const target = button.getAttribute('data-target')
        const isNumberTarget = target === 'numbers'

        const hasMatchingItem = this.selectors.items.some(item => {
          if (isNumberTarget) {
            // Regex is checking any numbers in the string.
            return (/\d/).test(item.id)
          }

          const formattedFirstCharacter = item.id.charAt(0).toLocaleLowerCase()

          return formattedFirstCharacter === target.toLowerCase()
        })

        if (!hasMatchingItem) {
          button.classList.add(classes.disabled)
          return
        }

        button.classList.remove(classes.disabled)
      })
    },

    /**
     * Create css variable for keys header.
     */
    async keysHeaderHeight() {
      await this.$nextTick()
      const height = this.selectors.keys.offsetHeight

      /**
       * If height is 0 then re-run function as it's a bug.
       */
      if (height === 0 && this.selectors.buttons.length) {
        setTimeout(() => {
          this.keysHeaderHeight()
        }, 100)

        return
      }

      /**
       * Set CSS variable.
       */
      document.documentElement.style.setProperty(
        '--main-glossary-keys-height',
        `${height}px`,
      )
    },

    /**
     * Scroll to the coresponded section.
     */
    scrollToSection() {
      this.selectors.buttons.forEach(button => {
        button.addEventListener('click', (event) => {
          if (this.activeButton) {
            this.activeButton.classList.remove(classes.active)
          }

          const targetLetter = event.target.getAttribute('data-target')
          const targetElement = this.findTargetElement(targetLetter)

          this.activeButton = button
          this.activeButton.classList.add(classes.active)

          if (!targetElement) {
            return
          }

          targetElement.scrollIntoView({
            block: 'start',
            behavior: 'smooth',
          })
        })
      })
    },
  },
}
</script>

<style lang="scss">
@import './main-glossary';
@import '~async/utils/divider-line/divider-line';
</style>
